/**
 * Data Room Summary Card
 *
 * Compact card showing data room completion status.
 * Used in profile screens and startup discovery cards.
 */

import { View, Text } from 'react-native';
import { useRouter } from 'expo-router';
import { FileText, ChevronRight, CheckCircle2, AlertCircle } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { calculateProgress, type FundingStage } from '@/lib/dataroom';
import type { DocumentUpload } from '@/lib/dataroom';
import AnimatedPressable from '@/components/AnimatedPressable';

interface DataRoomSummaryCardProps {
  uploads: Record<string, DocumentUpload>;
  stage: FundingStage;
  onPress?: () => void;
  compact?: boolean;
}

export function DataRoomSummaryCard({
  uploads,
  stage,
  onPress,
  compact = false,
}: DataRoomSummaryCardProps) {
  const router = useRouter();
  const colors = useThemeColors();

  const progress = calculateProgress(uploads, stage);
  const isComplete = progress.completionPercentage >= 80;

  const handlePress = () => {
    if (onPress) {
      onPress();
    } else {
      router.push('/(startup)/dataroom-checklist');
    }
  };

  if (compact) {
    return (
      <AnimatedPressable
        onPress={handlePress}
        className="flex-row items-center gap-3 p-3 rounded-xl"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <View
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: isComplete ? `${colors.success}15` : `${colors.warning}15` }}
        >
          {isComplete ? (
            <CheckCircle2 size={20} color={colors.success} />
          ) : (
            <AlertCircle size={20} color={colors.warning} />
          )}
        </View>
        <View className="flex-1">
          <Text style={{ color: colors.text.primary }} className="font-medium">
            Data Room
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            {progress.completionPercentage}% complete
          </Text>
        </View>
        <View className="items-end mr-2">
          <Text
            style={{ color: isComplete ? colors.success : colors.warning }}
            className="text-sm font-semibold"
          >
            {progress.uploadedDocuments}/{progress.requiredDocuments}
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            docs
          </Text>
        </View>
        <ChevronRight size={18} color={colors.text.tertiary} />
      </AnimatedPressable>
    );
  }

  return (
    <AnimatedPressable
      onPress={handlePress}
      className="p-4 rounded-2xl"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <View className="flex-row items-center justify-between mb-3">
        <View className="flex-row items-center gap-2">
          <View
            className="w-8 h-8 rounded-lg items-center justify-center"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <FileText size={18} color={colors.primary[600]} />
          </View>
          <Text style={{ color: colors.text.primary }} className="font-semibold">
            Data Room Status
          </Text>
        </View>
        <View className="flex-row items-center gap-1">
          <Text
            style={{ color: isComplete ? colors.success : colors.warning }}
            className="text-sm font-semibold"
          >
            {progress.completionPercentage}%
          </Text>
          <ChevronRight size={18} color={colors.text.tertiary} />
        </View>
      </View>

      {/* Progress Bar */}
      <View
        className="h-2 rounded-full overflow-hidden mb-3"
        style={{ backgroundColor: colors.surface.secondary }}
      >
        <View
          className="h-full rounded-full"
          style={{
            backgroundColor: isComplete ? colors.success : progress.completionPercentage >= 50 ? colors.warning : colors.error,
            width: `${progress.completionPercentage}%`,
          }}
        />
      </View>

      {/* Stats */}
      <View className="flex-row justify-between">
        <View>
          <Text style={{ color: colors.text.primary }} className="font-semibold">
            {progress.uploadedDocuments}
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            Uploaded
          </Text>
        </View>
        <View className="items-center">
          <Text style={{ color: colors.text.primary }} className="font-semibold">
            {progress.verifiedDocuments}
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            Verified
          </Text>
        </View>
        <View className="items-center">
          <Text style={{ color: colors.text.primary }} className="font-semibold">
            {progress.requiredDocuments}
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            Required
          </Text>
        </View>
        <View className="items-end">
          <Text style={{ color: colors.text.primary }} className="font-semibold">
            {progress.confidenceMultiplier.toFixed(2)}x
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            Confidence
          </Text>
        </View>
      </View>
    </AnimatedPressable>
  );
}
